import React, { useState } from "react"
import containerClasses from "../../styles/container.module.css"
import styles from "./BlephTypes.module.css"

type BlephType = {
  id: string
  title: string
  text: string
  image: string
  imageAlt: string
  videoUrl?: string
  videoLinkText?: string
}

type BlephTypesProps = {
  heading: string
  overline?: string
  subtitle?: string
  types: BlephType[]
}

export const BlephTypes = ({ heading, overline, subtitle, types }: BlephTypesProps) => {
  const [activeId, setActiveId] = useState(types[0]?.id)
  const activeType = types.find(t => t.id === activeId)

  return (
    <div className="bg-white">
      <div
        className={`${containerClasses.container} py-20 md:py-28 lg:py-32 xl:py-40`}
      >
        {/* Title row */}
        <div className="col-span-4 md:col-span-8 lg:col-span-12 flex flex-col items-center text-center mb-4 lg:mb-2">
          {overline && <p className="mt-0 mb-4 overline">{overline}</p>}
          <h2 className="mt-0 mb-6 fluid-heading-05 leading-tight max-w-xl mx-auto" dangerouslySetInnerHTML={{ __html: heading }} />
          {subtitle && (
            <p className="mt-0 mb-0 text-neutral-500 leading-relaxed max-w-xl mx-auto text-center">
              {subtitle}
            </p>
          )}
        </div>

        {/* Image — above selectors on mobile, right side on desktop */}
        <div className="col-span-4 md:col-span-8 lg:col-start-7 lg:col-span-6 flex flex-col justify-center mt-8 lg:mt-6 order-1 lg:order-2">
          {activeType && (
            <div className="bg-secondaryBrand-1 border-1 border-solid border-borderColour p-8 md:p-12 lg:p-16">
              <img
                loading="lazy"
                className="w-full"
                src={activeType.image}
                alt={activeType.imageAlt}
              />
              <p className="mt-4 mb-0 text-xs text-neutral-500 italic text-center">
                {activeType.title} blepharoplasty performed by Dr Elizabeth Hawkes FRCOphth MBBS BSc
              </p>
            </div>
          )}
        </div>

        {/* Selectors — below image on mobile, left side on desktop */}
        <div className="col-span-4 md:col-span-8 lg:col-span-5 flex flex-col lg:justify-center mt-8 lg:mt-6 order-2 lg:order-1">
          {types.map(type => (
            <div
              key={type.id}
              className={`transition-all duration-300 ${
                activeId === type.id
                  ? styles.selectorActive
                  : styles.selectorInactive
              }`}
            >
              <button
                onClick={() => setActiveId(type.id)}
                className={`text-left w-full px-6 pt-5 ${
                  type.videoUrl ? "pb-3" : "pb-5"
                }`}
              >
                <h3 className="mt-0 mb-2 text-xl sm:text-2xl font-serif font-bold">
                  {type.title} Blepharoplasty
                </h3>
                <div className={styles.selectorContent}>
                  <div dangerouslySetInnerHTML={{ __html: type.text }} />
                </div>
              </button>
              {type.videoUrl && (
                <div className="px-6 pb-5">
                  <a
                    href={type.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-base text-gold600 ${styles.videoLink}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      className={styles.videoIcon}
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    {type.videoLinkText || "Watch this procedure"}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </div>
  )
}

export default BlephTypes
